import { useEffect, useRef, useState } from "react";
import { Grip, Inbox, MessagesSquare, ShieldCheck, KeyRound, FileText, FileSpreadsheet, Presentation, HardDrive, FileType2 } from "lucide-react";
import { cn } from "@/lib/utils";

// Dependency-light Office-365 style app launcher for Connect. Kept
// self-contained (plain Tailwind + lucide, no shadcn Button / useDismiss)
// because Connect doesn't ship those primitives.

type SuiteAppId = "mail" | "connect" | "admin" | "identity" | "docs" | "sheet" | "present" | "drive" | "pdfViewer";

interface SuiteApp {
    id: SuiteAppId;
    name: string;
    blurb: string;
    url: string;
    tileClass: string;
    comingSoon?: boolean;
}

function env(key: string, fallback: string): string {
    const raw = (import.meta.env as Record<string, string | undefined>)[key];
    return raw && raw.trim().length > 0 ? raw : fallback;
}

const APPS: SuiteApp[] = [
    { id: "mail", name: "Mail", blurb: "Inbox, compose, schedule", url: env("VITE_MAIL_WEB_URL", "http://localhost:5180"), tileClass: "bg-blue-600 text-white" },
    { id: "connect", name: "Connect", blurb: "Chat, channels, meetings", url: env("VITE_CONNECT_WEB_URL", "http://localhost:5200"), tileClass: "bg-purple-600 text-white" },
    { id: "admin", name: "Admin", blurb: "Tenants, users, policies", url: env("VITE_ADMIN_WEB_URL", "http://localhost:5190"), tileClass: "bg-slate-700 text-white" },
    { id: "identity", name: "Account", blurb: "Sign-in, MFA, security", url: env("VITE_IDENTITY_WEB_URL", "http://localhost:5002"), tileClass: "bg-emerald-700 text-white" },
    { id: "docs", name: "Docs", blurb: "Word-compatible editor", url: env("VITE_DOCS_WEB_URL", "http://localhost:5210"), tileClass: "bg-sky-600 text-white" },
    { id: "sheet", name: "Sheet", blurb: "Excel-compatible", url: env("VITE_SHEET_WEB_URL", "http://localhost:5220"), tileClass: "bg-green-700 text-white" },
    { id: "present", name: "Slides", blurb: "PowerPoint-compatible", url: env("VITE_PRESENT_WEB_URL", "http://localhost:5232"), tileClass: "bg-orange-600 text-white" },
    { id: "drive", name: "Drive", blurb: "Files, folders, sharing", url: env("VITE_DRIVE_WEB_URL", "http://localhost:5240"), tileClass: "bg-indigo-600 text-white" },
    { id: "pdfViewer", name: "PDF Viewer", blurb: "Annotate + sign", url: env("VITE_PDF_WEB_URL", "http://localhost:5250"), tileClass: "bg-rose-600 text-white" }
];

const TILE_ICONS: Record<SuiteAppId, React.ComponentType<{ className?: string }>> = {
    mail: Inbox,
    connect: MessagesSquare,
    admin: ShieldCheck,
    identity: KeyRound,
    docs: FileText,
    sheet: FileSpreadsheet,
    present: Presentation,
    drive: HardDrive,
    pdfViewer: FileType2
};

export function AppLauncher({ currentApp }: { currentApp: SuiteAppId }) {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;
        const onDown = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") setOpen(false);
        };
        document.addEventListener("mousedown", onDown);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDown);
            document.removeEventListener("keydown", onKey);
        };
    }, [open]);

    return (
        <div ref={ref} className="relative">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-label="App launcher"
                aria-haspopup="menu"
                aria-expanded={open}
                className="flex h-10 w-10 items-center justify-center rounded-md hover:bg-accent"
            >
                <Grip className="h-5 w-5" />
            </button>
            {open ? (
                <div className="absolute left-12 top-0 z-50 w-[22rem] rounded-lg border bg-card p-3 text-card-foreground shadow-xl">
                    <nav aria-label="Athavita suite apps">
                    <header className="px-1 pb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        Athavita apps
                    </header>
                    <div className="grid grid-cols-3 gap-1.5">
                        {APPS.map((app) => {
                            const Icon = TILE_ICONS[app.id];
                            const isCurrent = app.id === currentApp;
                            const disabled = app.comingSoon || isCurrent;
                            const tileBody = (
                                <>
                                    <div
                                        className={cn(
                                            "flex h-9 w-9 shrink-0 items-center justify-center rounded-md",
                                            app.comingSoon ? "bg-muted text-muted-foreground" : app.tileClass
                                        )}
                                        aria-hidden
                                    >
                                        <Icon className="h-4 w-4" />
                                    </div>
                                    <div className="min-w-0 flex-1 text-left">
                                        <div className="flex items-center gap-1">
                                            <span className="truncate text-sm font-medium">{app.name}</span>
                                            {isCurrent ? (
                                                <span className="shrink-0 rounded-sm bg-primary/15 px-1 text-[10px] font-semibold uppercase tracking-wider text-primary">
                                                    Here
                                                </span>
                                            ) : null}
                                        </div>
                                        <div className="truncate text-[11px] text-muted-foreground">
                                            {app.comingSoon ? "Coming soon" : app.blurb}
                                        </div>
                                    </div>
                                </>
                            );
                            const className = cn(
                                "flex items-center gap-2 rounded-md p-2",
                                disabled ? "cursor-default opacity-60" : "hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                            );
                            return disabled ? (
                                <div
                                    key={app.id}
                                    aria-disabled={true}
                                    aria-label={`${app.name} — ${app.comingSoon ? "coming soon" : "current app"}`}
                                    className={className}
                                >
                                    {tileBody}
                                </div>
                            ) : (
                                <a
                                    key={app.id}
                                    href={app.url}
                                    target="_blank"
                                    rel="noreferrer noopener"
                                    aria-label={`Open ${app.name} in a new tab — ${app.blurb}`}
                                    className={className}
                                    onClick={() => setOpen(false)}
                                >
                                    {tileBody}
                                </a>
                            );
                        })}
                    </div>
                    <footer className="mt-2 border-t pt-2 text-center text-[10px] text-muted-foreground">
                        Grey tiles are coming soon.
                    </footer>
                    </nav>
                </div>
            ) : null}
        </div>
    );
}
